import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { centralApi } from '../AxiosInstance/CreateInstance';
import Card, { CardBody, CardHeader } from '../../components/bootstrap/Card';
import { toast } from 'react-toastify';
import PageWrapper from '../../layout/PageWrapper/PageWrapper';
import Page from '../../layout/Page/Page';
import { Department, FeedbackForm } from '../Role_Layout/roleTypes';
import { Briefcase, Building, CalendarCheck, ClipboardList } from 'lucide-react';
import SubHeader, { SubHeaderLeft } from '../../layout/SubHeader/SubHeader';
import Button from '../../components/bootstrap/Button';
import dayjs from 'dayjs';

interface IFeedbackViewProp {
	isFluid?: boolean;
}

const FeedbackView: React.FC<IFeedbackViewProp> = ({ isFluid }) => {
	const [feedback, setFeedback] = useState<FeedbackForm>();
	const [department, setDepartment] = useState<Department>();
	const { id, roundType } = useParams();
	const navigate = useNavigate();

	useEffect(() => {
		const fetchFeedback = async () => {
			try {
				const res = await centralApi.post('jobService/feedbackFormTemplate/getById', {
					evaluationFormTemplateId: id,
				});
				setFeedback(res.data.data);
			} catch (error) {
				toast.error('Failed to fetch feedback parameter', { closeOnClick: true });
			}
		};

		fetchFeedback();
	}, [id]);

	useEffect(() => {
		const fetchDepartment = async () => {
			if (feedback?.departmentId) {
				try {
					const res = await centralApi.get('userService/department/list');
					const matched = res.data.data.find(
						(item: any) => item._id === feedback.departmentId,
					);
					setDepartment(matched);
				} catch (error) {
					toast.error('Failed to fetch department', { closeOnClick: true });
				}
			}
		};

		fetchDepartment();
	}, [feedback?.departmentId]);

	const backgroundStyles = [
		"#0f1628 "
	];

	const borderColors = [
		'#fb923c', // Orange
		'#60a5fa', // Blue
		'#34d399', // Green
	];

	return (
		<Page>
			<Card stretch={isFluid} className='shadow rounded-4 border-0 bg-dark'>
				<CardHeader borderSize={1} className=' rounded-top-4'>
					<div className='text-center'>
						<h3 className='fw-bold text-light'>
							<ClipboardList size={24} className='text-info me-2' />
							Feedback Form Configuration
						</h3>
					</div>
				</CardHeader>
				<SubHeaderLeft className='mt-2 ms-3 bg-dark'>
					<Button
						icon='ArrowBack'
						onClick={() => navigate('/jobRequisition')}
						className='hrms-gradient-btn'>
						Back to List
					</Button>
				</SubHeaderLeft>

				<CardBody className='bg-dark rounded-bottom-4 p-4'>
					<div className='bg-body-secondary p-4 rounded-4 mb-4 shadow-sm border border-light-subtle'>
						<div className='row text-start gy-3'>
							<div className='col-md-4 d-flex align-items-start gap-3'>
								<CalendarCheck size={20} className='text-primary mt-1' />
								<div>
									<small className='text-muted'>Linked Round</small>
									<div className='fw-semibold'>{roundType}</div>
								</div>
							</div>
							<div className='col-md-4 d-flex align-items-start gap-3'>
								<Briefcase size={20} className='text-success mt-1' />
								<div>
									<small className='text-muted'>Role Title</small>
									<div className='fw-semibold'>
										{feedback?.roleTitle}
									</div>
								</div>
							</div>
							<div className='col-md-4 d-flex align-items-start gap-3'>
								<Building size={20} className='text-purple mt-1' />
								<div>
									<small className='text-muted'>Department</small>
									<div className='fw-semibold'>{department?.name}</div>
								</div>
							</div>
						</div>
					</div>

					{feedback?.parameterDetails?.map((group, idx) => (
						<div
							className='p-4 mb-4 rounded-4 shadow-sm border'
							style={{
								backgroundColor:
									backgroundStyles[idx % backgroundStyles.length],
								borderLeft: `6px solid ${borderColors[idx % borderColors.length]}`,
							}}>
							<div className='d-flex justify-content-between align-items-center mb-3'>
								<h6 className='fw-bold mb-0 text-light'>{group.group_title}</h6>
								<span className='badge rounded-pill bg-dark border text-light fw-semibold'>
									{group?.weightage}% Weight
								</span>
							</div>
							<ul className='ps-3 mb-0'>
								{group?.parameters?.map((param, i) => (
									<li className='text-light fw-medium'>{param}</li>
								))}
							</ul>
						</div>
					))}

					<div className='bg-body-secondary p-4 rounded-4 mt-4 shadow-sm border border-light-subtle'>
						<div className='row mb-3'>
							<div className='col-md-6'>
								<small className='text-muted'>Created By</small>
								<div className='fw-semibold text-light'>
									{feedback?.createdBy.name}
								</div>
							</div>
							<div className='col-md-6'>
								<small className='text-muted'>Created Date</small>
								<div className='fw-semibold text-light'>
									{feedback?.createdAt
										? dayjs(feedback?.createdAt).format('D MMMM YYYY')
										: ''}
								</div>
							</div>
						</div>
						<hr />
						<small className='text-muted'>Description</small>
						<div className='fw-medium text-light'>
							{feedback?.description}
						</div>
					</div>
				</CardBody>
			</Card>
		</Page>
	);
};

FeedbackView.defaultProps = {
	isFluid: false,
};

export default FeedbackView;
